import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { contactsApi } from 'services/contacts';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachInstruction, extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { trackEvent } from 'helpers/analytics';
import { findNode, isReorderNoOp, isCombineNoOp } from '../treeHelpers';

const buildMoveEventProps = (networkId, dragged, originalParentId, destinationParentId, operation, payloadCommon) => ({
  collectionId: networkId,
  itemType: dragged.type,
  itemId: dragged.id,
  fromParentId: originalParentId,
  toParentId: destinationParentId,
  operation,
  ...(payloadCommon?.beforeId ? { beforeId: payloadCommon.beforeId } : undefined),
  ...(payloadCommon?.afterId ? { afterId: payloadCommon.afterId } : undefined),
  page: 'lists',
});

export function useTreeNodeDnD({
  item,
  level,
  isFolder,
  isExpanded,
  parentIndex,
  tree,
  expandFolder,
  moveListToFolder,
  moveFolder,
  networkId,
}) {
  const rowRef = useRef(null);
  const [instruction, setInstruction] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;

    // Persist the move to the backend and invalidate tags
    const persistMove = async ({ dragged, destinationParentId, payloadCommon }) => {
      if (dragged.type === 'list') {
        await moveListToFolder({
          listId: dragged.id,
          folderId: destinationParentId,
          ...payloadCommon,
          networkId,
        });
      } else if (dragged.type === 'folder') {
        await moveFolder({ folderId: dragged.id, parentId: destinationParentId, ...payloadCommon, networkId });
      }
      dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
    };

    const cleanupDraggable = draggable({
      element: el,
      getInitialData: () => ({
        id: item.id,
        type: item.type,
        currentParentId: parentIndex[`${item.type}:${item.id}`]?.parentId ?? null,
      }),
    });

    const cleanupDropTarget = dropTargetForElements({
      element: el,
      getData: ({ element, input }) => {
        const operations = {
          'reorder-before': 'available',
          'reorder-after': 'available',
          combine: isFolder ? 'available' : 'blocked',
        };
        return attachInstruction(
          {
            targetId: item.id,
            targetType: item.type,
            targetParentId: parentIndex[`${item.type}:${item.id}`]?.parentId ?? null,
            targetLevel: level,
          },
          { element, input, operations, axis: 'vertical' },
        );
      },
      onDragEnter: ({ self }) => {
        const inst = extractInstruction(self.data);
        setInstruction(inst);
        if (isFolder && inst && !inst.blocked && inst.operation === 'combine' && !isExpanded) {
          expandFolder(item.id);
        }
      },
      onDragLeave: () => {
        setInstruction(null);
      },
      onDrag: ({ self }) => {
        setInstruction(extractInstruction(self.data));
      },
      onDrop: async ({ source, self }) => {
        setInstruction(null);

        const dragged = source.data || {};
        if (!dragged || dragged.id == null) return undefined;
        if (dragged.id === item.id && dragged.type === item.type) return undefined;

        const inst = extractInstruction(self.data);
        if (!inst || inst.blocked) return undefined;

        const currentTree = tree;
        const draggedNode = findNode(currentTree, dragged.id, dragged.type);
        if (!draggedNode) return undefined;

        // Local reorder + persist with beforeId/afterId
        if (inst.operation === 'reorder-before' || inst.operation === 'reorder-after') {
          const destinationParentId = parentIndex[`${item.type}:${item.id}`]?.parentId ?? null;
          // Prefer subtree check to avoid parentIndex anomalies
          const wouldCreateCycle = !!(
            dragged.type === 'folder' &&
            destinationParentId != null &&
            findNode([draggedNode], destinationParentId, 'folder')
          );
          if (wouldCreateCycle) return undefined;

          // Determine if drop results in the same position (no-op)
          const originalParentId = dragged.currentParentId ?? null;
          const isNoOp = isReorderNoOp(currentTree, originalParentId, destinationParentId, dragged, item, inst);
          const payloadCommon = inst.operation === 'reorder-before' ? { beforeId: item.id } : { afterId: item.id };
          if (isNoOp) return undefined;
          try {
            await persistMove({ dragged, payloadCommon });
            trackEvent(
              'lists:move_item',
              buildMoveEventProps(
                networkId,
                dragged,
                originalParentId,
                destinationParentId,
                inst.operation,
                payloadCommon,
              ),
            );
          } catch {
            // noop: service-level optimistic update will rollback
          }
          return undefined;
        }

        // Combine into folders with optimistic backend call
        if (inst.operation === 'combine') {
          if (!isFolder) return undefined;
          // Prevent combining folder into its own subtree
          const targetInDraggedSubtree = !!(dragged.type === 'folder' && findNode([draggedNode], item.id, 'folder'));
          if (targetInDraggedSubtree) return undefined;
          const destinationParentId = item.id;

          const originalParentId = dragged.currentParentId ?? null;
          const isNoOp = isCombineNoOp(currentTree, originalParentId, destinationParentId, dragged);
          if (isNoOp) return undefined;
          try {
            await persistMove({ dragged, destinationParentId });
            trackEvent(
              'lists:move_item',
              buildMoveEventProps(networkId, dragged, originalParentId, destinationParentId, 'combine'),
            );
          } catch {
            // noop: service-level optimistic update will rollback
          }
          return undefined;
        }
        return undefined;
      },
    });

    return () => {
      cleanupDropTarget();
      cleanupDraggable();
    };
  }, [
    item,
    level,
    isFolder,
    isExpanded,
    parentIndex,
    tree,
    expandFolder,
    moveListToFolder,
    moveFolder,
    networkId,
    dispatch,
  ]);

  return { rowRef, instruction };
}
